
import { PlusOutlined, EyeOutlined } from '@ant-design/icons-vue';
import { Entity } from '@kunlun/meta';
import { OioIcon } from '@kunlun/vue-ui-common';
import { OioModal } from '@kunlun/vue-ui-antd';
import { isString } from 'lodash-es';
import { computed, defineComponent, PropType, ref, watch } from 'vue';
import { UploadCommonProps, useMetadataProps } from '@kunlun/dependencies';
import FileUpload from './components/file.vue';
import draggable from 'vuedraggable';

export default defineComponent({
  name: 'UploadImg',
  components: {
    FileUpload,
    OioModal,
    OioIcon,
    PlusOutlined,
    EyeOutlined,
    draggable
  },
  inheritAttrs: false,
  props: {
    ...UploadCommonProps,
    value: {
      type: [Array, Object, String] as PropType<string | (string | object)[]>
    },
    showPreviewTitle: {
      type: Boolean,
      default: true
    }
  },
  emits: ['change', 'remove'],
  setup(props, { emit }) {
    const previewVisible = ref(false);
    const previewTitle = ref<string | undefined>('');
    const previewImage = ref<string | undefined>('');
    const { readonly, disabled } = useMetadataProps(props, true);

    const fileList = computed(() => {
      const value = props.value;
      let currentValues: (string | object)[] | undefined;
      if (Array.isArray(value)) {
        currentValues = value;
      } else if (value != null) {
        currentValues = [value];
      }
      return (currentValues || []).map((a) => {
        if (isString(a)) {
          return { url: a, type: 'image/*' };
        }
        return { ...(a as Entity), type: 'image/*' };
      });
    });

    const innerFileList = ref(fileList.value);

    watch(fileList, (newList) => {
      innerFileList.value = [...newList];
    });

    const handlePreview = (file) => {
      if (!file.url && !file.preview) return;
      previewTitle.value = props.showPreviewTitle ? file.name || file.url || file.preview : '';
      previewImage.value = file.url || file.preview;
      previewVisible.value = true;
    };

    const handleCancel = () => {
      previewVisible.value = false;
    };

    const onChangeFile = (v) => {
      innerFileList.value = v;
      props.change?.(v);
    };

    const onRemoveFile = (v) => {
      const updated = innerFileList.value.filter((item) => item !== v);
      innerFileList.value = updated;
      props.remove?.(v);
      emit('remove', updated);
    };

    const onDragEnd = () => {
      props.change?.(innerFileList.value);
      emit('change', innerFileList.value);
    };

    return {
      previewVisible,
      previewTitle,
      previewImage,
      readonly,
      disabled,
      fileList,
      innerFileList,
      handlePreview,
      handleCancel,
      onChangeFile,
      onRemoveFile,
      onDragEnd
    };
  }
});
